// Landing page inspired by Photo AI design, adapted for Mapas KM
import { createElement } from '@/utils/dom'
import { router } from '@/utils/router'
import { Footer } from '@/components/footer'

export class LandingPage {

  async render(): Promise<HTMLElement> {
    const page = createElement('div', { className: 'landing-page' })

    // Navigation
    page.appendChild(this.createNavigation())

    // Hero section
    page.appendChild(this.createHeroSection())

    // Video tutorial section
    page.appendChild(this.createVideoSection())

    // Features section
    page.appendChild(this.createFeaturesSection())

    // Trust indicators
    page.appendChild(this.createTrustSection())

    // Footer
    const footer = new Footer()
    page.appendChild(footer.render())

    return page
  }

  private createNavigation(): HTMLElement {
    const nav = createElement('nav', { className: 'nav' })

    nav.innerHTML = `
      <div class="container">
        <div class="nav-container">
          <div class="nav-left">
            <a href="#" class="nav-logo">
              🗺️ Mapas KM
            </a>
            <ul class="nav-menu">
              <li><a href="/" class="nav-link active">Início</a></li>
              <li><a href="/pricing" class="nav-link">Preços</a></li>
              <li><a href="/funcionalidades" class="nav-link">Funcionalidades</a></li>
            </ul>
          </div>
          <div class="nav-right">
            <a href="/register" class="nav-cta">Começar Grátis →</a>
            <a href="/login" class="nav-login">Login</a>
          </div>
          <button class="mobile-menu-toggle" aria-label="Menu">
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
        
        <!-- Mobile Menu -->
        <div class="mobile-menu">
          <div class="mobile-menu-header">
            <div class="mobile-menu-logo">🗺️ Mapas KM</div>
            <button class="mobile-menu-close" aria-label="Fechar menu">✕</button>
          </div>
          <div class="mobile-menu-content">
            <div class="mobile-menu-nav">
              <a href="/" class="mobile-nav-link active">Início</a>
              <a href="/pricing" class="mobile-nav-link">Preços</a>
              <a href="/funcionalidades" class="mobile-nav-link">Funcionalidades</a>
            </div>
            <div class="mobile-menu-actions">
              <a href="/register" class="mobile-nav-cta">Começar Grátis →</a>
              <a href="/login" class="mobile-nav-login">Login</a>
            </div>
          </div>
        </div>
      </div>
    `

    // Add click handlers
    nav.addEventListener('click', (e) => {
      const target = e.target as HTMLElement

      // Handle mobile menu toggle
      if (target.closest('.mobile-menu-toggle')) {
        e.preventDefault()
        const mobileMenu = nav.querySelector('.mobile-menu') as HTMLElement
        const isOpen = mobileMenu.classList.contains('open')

        if (isOpen) {
          mobileMenu.classList.remove('open')
          document.body.classList.remove('mobile-menu-open')
        } else {
          mobileMenu.classList.add('open')
          document.body.classList.add('mobile-menu-open')
        }
        return
      }

      // Handle mobile menu close button
      if (target.closest('.mobile-menu-close')) {
        e.preventDefault()
        const mobileMenu = nav.querySelector('.mobile-menu') as HTMLElement
        mobileMenu.classList.remove('open')
        document.body.classList.remove('mobile-menu-open')
        return
      }

      // Handle navigation links
      if (target.tagName === 'A') {
        e.preventDefault()
        const href = target.getAttribute('href')

        // Close mobile menu if open
        const mobileMenu = nav.querySelector('.mobile-menu') as HTMLElement
        mobileMenu.classList.remove('open')
        document.body.classList.remove('mobile-menu-open')

        if (href === '/pricing' || href === '/dashboard' || href === '/funcionalidades' || href === '/login' || href === '/register') {
          router.navigate(href)
        } else if (href === '#' && target.classList.contains('nav-logo')) {
          router.navigate('/')
        }
      }
    })

    return nav
  }

  private createHeroSection(): HTMLElement {
    const hero = createElement('section', { className: 'hero' })

    hero.innerHTML = `
      <div class="container">
        <div class="hero-grid">
          <!-- Left column: Existing content -->
          <div class="hero-content">
            
            <h1 class="hero-title">
            🔥 Simulador de Rotas Empresariais
            </h1>
            
            <div class="hero-subtitle">
            ⚡ <strong>Simule rotas de colaboradores</strong> → Automatize a análise de padrões de viagem<br>
            📊 <strong>Exportação para Excel</strong> → Relatórios organizados para análise interna
            </div>


            
            <div style="color: var(--text-muted); font-size: 0.875rem;">
              ⭐ 1.235 simulações de rota geradas este mês
            </div>
          </div>

          <!-- Right column: Signup box -->
          <div class="signup-container">

            
            <!-- White background box for fancy effect -->
            <div class="signup-white-background"></div>
            
            <div class="signup-box">
              <div class="signup-box-content">
              <input 
                type="text" 
                placeholder="Digite o nif da sua empresa..." 
                class="signup-email-input"
                id="signup-nif"
                maxlength="9"
                pattern="[0-9]{9}"
                style="color: #000000 !important;"
              >
              <div class="form-error" id="signup-nif-error" style="display: none;"></div>
              
              <button class="btn btn-cta-flash" id="signup-primary-cta">
                <i class="fa-solid fa-rocket"></i>
                Comece já a simular relatórios →
              </button>
              
              <div class="signup-guarantee">
                💰 Poupe 4+ horas todos os meses, garantido
              </div>
              

              <div class="signup-footer">
                Junte-se às empresas portuguesas que já poupam tempo.
              </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `



    // Add navigation functionality for signup box (right column)
    const signupCtaButton = hero.querySelector('#signup-primary-cta') as HTMLButtonElement
    const nifInput = hero.querySelector('#signup-nif') as HTMLInputElement

    // Add input formatting for NIF (only numbers)
    nifInput.addEventListener('input', (e) => {
      const input = e.target as HTMLInputElement
      input.value = input.value.replace(/\D/g, '') // Only numbers
    })

    signupCtaButton.addEventListener('click', (e) => {
      e.preventDefault()

      const nifValue = nifInput.value.trim()

      // Navigate to register page with NIF parameter if provided
      if (nifValue) {
        router.navigate(`/register?nif=${encodeURIComponent(nifValue)}`)
      } else {
        router.navigate('/register')
      }
    })

    return hero
  }

  private createVideoSection(): HTMLElement {
    const videoSection = createElement('section', {
      className: 'section video-section',
      id: 'video-tutorial'
    })

    videoSection.innerHTML = `
      <div class="container">
        <div class="text-center mb-4">
          <a href="https://www.youtube.com/watch?v=eeQFdrXeA-k" target="_blank" rel="noopener noreferrer" class="btn btn-cta-flash video-banner">
            <i class="fa-brands fa-youtube"></i> Veja Como Funciona
          </a>
        </div>
        
        <div class="video-container">
          <iframe 
            src="https://www.youtube.com/embed/eeQFdrXeA-k" 
            title="Tutorial Mapas KM"
            frameborder="0" 
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
            referrerpolicy="strict-origin-when-cross-origin"
            allowfullscreen
          ></iframe>
        </div>
      </div>
    `

    return videoSection
  }

  private createFeaturesSection(): HTMLElement {
    const features = createElement('section', {
      className: 'section',
      id: 'features'
    })

    features.innerHTML = `
      <div class="container">
        <div class="text-center mb-8">
          <h2>Por que as empresas portuguesas escolhem o Mapas KM</h2>
          <p>Automatize a análise das deslocações empresariais dos seus colaboradores</p>
        </div>
        
        <div class="grid grid-2">
          <div class="card">
            <div style="font-size: 2rem; margin-bottom: 1rem;">🚗</div>
            <h3>Simulação de Rotas</h3>
            <p>O nosso algoritmo simula as rotas de viagem de negócios mais frequentes dos colaboradores, baseado em padrões reais de deslocação empresarial.</p>
          </div>
          
          <div class="card">
            <div style="font-size: 2rem; margin-bottom: 1rem;">📊</div>
            <h3>Exportação para Excel</h3>
            <p>Relatórios profissionais com simulações de rotas exportados em formato Excel, organizados para análise e ajustes internos.</p>
          </div>
        </div>
      </div>
    `

    return features
  }

  private createTrustSection(): HTMLElement {
    const trust = createElement('section', { className: 'trust-indicators' })

    trust.innerHTML = `
      <div class="container">
        <div class="text-center">
          <p style="color: var(--text-secondary); margin-bottom: 2rem;">
          A confiança das empresas portuguesas para simulação de rotas empresariais
          </p>
          
          <div class="trust-logos">
            <div style="padding: 1rem 2rem; background: var(--surface-dark); border-radius: 0.5rem; border: 1px solid var(--border-color);">
              🏢 Usado por +500 Empresas
            </div>
          </div>
        </div>
        
        <div style="margin-top: 4rem; padding: 3rem; background: var(--gradient-card); border-radius: 1rem; text-align: center; border: 1px solid var(--border-color);">
        <h3>Pronto para automatizar a simulação das suas rotas empresariais?</h3>
          <button class="btn btn-primary btn-lg" id="bottom-cta">
            Ver Planos de Preços →
          </button>
        </div>
      </div>
    `

    // Add bottom CTA functionality
    const bottomCta = trust.querySelector('#bottom-cta') as HTMLButtonElement
    bottomCta.addEventListener('click', () => {
      router.navigate('/pricing')
    })

    return trust
  }


}
